// Reliance and KSHEMA in three stages — what the History dialog shows and allows. The server sends, per part,
// { key, label, company, stages: [{ stage, label, status, proposalNumber?, link?, mode?, error?, at? }], walletPaid }
// (relianceStages; company "Reliance" / "KSHEMA");
// the rules themselves live on the server (shared/relianceStagesCore.js).
import { TONES } from "./policyParts";

export const STAGE_ORDER = ["proposal", "kyc", "payment"];
const STAGE_NAME = { proposal: "Proposal", kyc: "KYC", payment: "Payment" };

export const stageName = (stage) => STAGE_NAME[stage] || String(stage || "");

const normStatus = (status) => {
  const s = String(status || "pending").trim().toLowerCase();
  return ["completed", "failed"].includes(s) ? s : "pending";
};

/** Label and colours of one stage's status (the same tones as the part lines). */
export const stageStatusMeta = (status) => {
  const s = normStatus(status);
  if (s === "completed") return { status: s, label: "Completed", ...TONES.success };
  if (s === "failed") return { status: s, label: "Failed", ...TONES.danger };
  return { status: s, label: "Pending", ...TONES.muted };
};

/** The insurer a part's steps belong to ("Reliance" / "KSHEMA"); Reliance when the server did not say. */
export const insurerOf = (part) => (part && part.company) || "Reliance";

const isKshema = (company) => /kshema/i.test(String(company || ""));

/**
 * "1 Proposal", "2 KYC", "3 Payment link" / "3 Wallet payment" (the payment says how, once it is known).
 * KSHEMA's payment is its QR page ("3 Payment QR") for a link policy and its CD Balance ("3 Wallet payment") for a wallet
 * policy (2026-10-22). A step that already ran says what it did (its recorded mode); one that has not follows the policy's method.
 */
export const stageTitle = (row, { paymentMethod, company } = {}) => {
  const n = STAGE_ORDER.indexOf(row.stage) + 1;
  if (row.stage === "payment") {
    const recorded = String(row.mode || "").toLowerCase();
    if (isKshema(company)) {
      const wallet = recorded ? recorded === "wallet" : String(paymentMethod || "").toLowerCase() === "wallet";
      return `${n} ${wallet ? "Wallet payment" : "Payment QR"}`;
    }
    if (recorded === "qr") return `${n} Payment QR`;
    const mode = String(row.mode || paymentMethod || "").toLowerCase();
    return `${n} ${mode === "link" ? "Payment link" : mode ? "Wallet payment" : "Payment"}`;
  }
  return `${n} ${stageName(row.stage)}`;
};

/** The small grey detail after the status: the proposal number, or why it failed. */
export const stageDetail = (row) => {
  if (row.stage === "proposal" && row.proposalNumber) return row.proposalNumber;
  if (normStatus(row.status) === "failed" && row.error) return row.error;
  return "";
};

/**
 * Can this stage be clicked to go back to Pending? Only a Completed or Failed one — a Pending stage is never clickable,
 * so nothing can ever be set to Completed by hand. Returns { ok, why } (why = the tooltip when it cannot).
 */
export const setBackState = (row, { enabled = true, walletPaid = false, insurer = "Reliance" } = {}) => {
  const s = normStatus(row.status);
  if (s === "pending") return { ok: false, why: "Pending - the automation does this step" };
  if (walletPaid) return { ok: false, why: `Paid from the wallet at ${insurer} - its stages cannot be redone` };
  if (!enabled) return { ok: false, why: "The automation is running this policy - try again when it has finished" };
  return { ok: true, why: "Click to set back to Pending" };
};

/** Only a proposal takes the later steps along (a new referral number = a new KYC and payment); KYC / Payment alone. */
const cascadesFrom = (stage) => (stage === "proposal" ? STAGE_ORDER.slice(1).map(stageName) : []);

/** The confirm box's question: this stage (and, for the proposal, KYC and Payment) go back to Pending. */
export const setBackQuestion = (stage, insurer = "Reliance") => {
  const later = cascadesFrom(stage);
  const also = later.length ? ` ${later.join(" and ")} will also go back to Pending.` : "";
  const proposal = stage === "proposal" ? ` The next run makes a NEW proposal at ${insurer}.` : "";
  return `Set ${stageName(stage)} back to Pending?${also}${proposal}`;
};

/** The parts that have stages to show (Reliance and KSHEMA parts; the server sends nothing for other insurers). */
export const stageParts = (relianceStages) =>
  (Array.isArray(relianceStages) ? relianceStages : []).filter((p) => p && Array.isArray(p.stages) && p.stages.length);

/** The first stage that is not completed — the one the next run works on ("Up next"); null when all are done. */
export const nextStage = (stages) => {
  const row = (stages || []).find((s) => stageStatusMeta(s.status).status !== "completed");
  return row ? row.stage : null;
};

/** How many of a part's stages are completed. */
export const doneCount = (stages) => (stages || []).filter((s) => stageStatusMeta(s.status).status === "completed").length;


/**
 * May "Update & run" be offered? Only when the policy's automation is not working on it: Completed, Pending (queued) or
 * Failed. Not while it runs (a run cannot be stopped half way) and not on hold (it would only wait there).
 */
export const canRunNow = (jobStatus) => {
  const s = String(jobStatus || "").trim().toLowerCase();
  return ["completed", "complete", "completed_with_errors", "pending"].includes(s) || s === "failed" || s.startsWith("failed_");
};

/** Is the automation running the policy right now? */
export const isRunningNow = (jobStatus) => String(jobStatus || "").trim().toLowerCase() === "processing";

/** The one-line note next to the confirm buttons: what else goes back to Pending. */
export const resetNote = (stage) => {
  if (stage === "proposal") return "KYC and Payment also go back to Pending · a NEW proposal is made";
  if (stage === "kyc") return "Only the KYC is redone · the payment is not repeated if it is done";
  return "Only this step goes back to Pending";
};

/* ---------------- Edit -> Status: steps set by hand (the server's shared/relianceStagesCore.js whyNotSetByHand) --------------- */

/** A Reliance referral number: R and digits (R141026119597). */
export const REFERRAL_NUMBER = /^R\d{6,}$/i;
const PAGE_LINK = /^https?:\/\/\S+$/i;

/**
 * Why a step cannot be set to `to` by hand in the Status dialog — null when it can. The same sentences as the server,
 * which checks again: a Proposal needs its referral number (KSHEMA its page link), KYC needs the Proposal completed
 * (already or in this save), a wallet payment never.
 *
 * @param {Object} o  part (with company / stages / walletPaid), stage, to, number, link, paymentMethod, proposalDone
 */
export const manualStepProblem = ({ part, stage, to, number = "", link = "", paymentMethod = "", proposalDone } = {}) => {
  const row = ((part && part.stages) || []).find((s) => s.stage === stage) || {};
  const now = stageStatusMeta(row.status).status;
  if (part && part.walletPaid) return `This policy was paid from the wallet at ${insurerOf(part)}, so its steps cannot be changed`;
  if (to === "pending") return now === "pending" ? `${stageName(stage)} is already pending` : null;
  if (to !== "completed") return `${stageName(stage)} can only be set to Pending or Completed by hand`;
  if (stage === "proposal") {
    if (isKshema(part && part.company)) {
      const page = String(link || row.link || "").trim();
      if (!PAGE_LINK.test(page)) return "Proposal can be Completed only with the KSHEMA proposal page link. Paste the page address";
      if (now === "completed" && page === String(row.link || "").trim()) return "Proposal is already completed with this page";
      return null;
    }
    const n = String(number || row.proposalNumber || "").trim();
    if (!n) return "Proposal can be Completed only with its referral number. Type the referral number";
    if (!REFERRAL_NUMBER.test(n)) return `"${n}" is not a referral number. It starts with R and has digits only, like R141026119597`;
    if (now === "completed" && n.toUpperCase() === String(row.proposalNumber || "").trim().toUpperCase()) {
      return "Proposal is already completed with this referral number";
    }
    return null;
  }
  if (now === "completed") return `${stageName(stage)} is already completed`;
  if (stage === "kyc") {
    const done = proposalDone === undefined ? stageOf(part, "proposal") === "completed" : !!proposalDone;
    return done ? null : "KYC can be Completed only after the Proposal is completed. Complete the Proposal with its referral number first";
  }
  if (!isKshema(part && part.company) && String(paymentMethod || "").toLowerCase() === "wallet") {
    return "A wallet payment cannot be marked by hand. Money moves at Reliance, so only the automation records it";
  }
  return null;
};

/** The status of one step of a part ("pending" when missing). */
export const stageOf = (part, stage) =>
  stageStatusMeta((((part && part.stages) || []).find((s) => s.stage === stage) || {}).status).status;

/** The changes the Status dialog sends: only the steps whose chosen status differs (a new referral number counts). */
export const manualChanges = (part, picked = {}, { number = "", link = "" } = {}) =>
  STAGE_ORDER.filter((stage) => picked[stage] && (picked[stage] !== stageOf(part, stage) || (stage === "proposal" && picked[stage] === "completed" && newProposalId(part, number, link))))
    .map((stage) => ({
      stage,
      status: picked[stage],
      ...(stage === "proposal" && picked[stage] === "completed" ? (isKshema(part && part.company) ? { link: link.trim() } : { proposalNumber: number.trim().toUpperCase() }) : {}),
    }));

const newProposalId = (part, number, link) => {
  const row = ((part && part.stages) || []).find((s) => s.stage === "proposal") || {};
  const typed = String(isKshema(part && part.company) ? link : number).trim().toUpperCase();
  const saved = String((isKshema(part && part.company) ? row.link : row.proposalNumber) || "").trim().toUpperCase();
  return !!typed && typed !== saved;
};

/** What a manual-change log entry says in words: "Proposal pending → completed". */
export const logChangeWords = (c) => `${stageName(c.stage)} ${c.from || "pending"} → ${c.to}`;
